import type { GetContentArtifactContentQuery } from "src/graphql";
import type {
  SmsChannelSchema,
  PushChannelSchema,
} from "@hightouch/lib/llm/lifecycle-studio/channels";

/**
 * Status of a content email (generation state)
 */
export const ContentEmailStatus = {
  Generating: "generating",
  // A run is re-generating content that already has a version — the current
  // version stays the content, so this renders as ready plus an updating
  // affordance, never as a blocking loading state.
  Editing: "editing",
  Completed: "completed",
  Failed: "failed",
  // Set when the generation job is canceled (see backend JobStatus.Cancelled,
  // surfaced via handleContentInteractionFailure).
  Canceled: "cancelled",
} as const;

export type ContentEmailStatus =
  (typeof ContentEmailStatus)[keyof typeof ContentEmailStatus];

/**
 * A content email whose generation reached a terminal failure (failed or
 * canceled) before producing a version. The UI surfaces an error instead of
 * waiting on a version that will never arrive.
 */
export function isFailedContentEmailStatus(
  status: string | null | undefined,
): boolean {
  return (
    status === ContentEmailStatus.Failed ||
    status === ContentEmailStatus.Canceled
  );
}

/**
 * A content email with usable content: `editing` means a run is re-generating
 * content whose current version is intact, so surfaces that render or act on
 * the existing content (editor chrome, version banner, export) treat it
 * exactly like `completed`.
 */
export function isReadyContentEmailStatus(
  status: string | null | undefined,
): boolean {
  return (
    status === ContentEmailStatus.Completed ||
    status === ContentEmailStatus.Editing
  );
}

/** Artifact content response from getContentArtifactContent resolver */
export type ArtifactContent = NonNullable<
  GetContentArtifactContentQuery["getContentArtifactContent"]
>;

/** ArtifactContent with the opaque JSON `content` field typed to our local content shapes. */
export type TypedArtifactContent = Omit<ArtifactContent, "content"> & {
  content: SmsChannelSchema | PushChannelSchema | null;
};
